// src/components/admin/page-management/section-forms/ImageForm.js
import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Upload, Image, Palette, AlignLeft, AlignCenter, AlignRight, Settings, Sparkles, Wand2, RotateCcw, Link2, Type } from 'lucide-react';
import ImageUploader from '../ImageUploader';

const ImageForm = ({ content = {}, updateContent = () => {}, pageSlug = 'demo' }) => {
  const [activeSection, setActiveSection] = useState('basic');
  const [showPreview, setShowPreview] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const handleImageUpload = (imagePath) => {
    console.log('ImageForm handling image upload with path:', imagePath);
    updateContent('imageUrl', imagePath);
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 600);
  };

  // Handle image overlay
  const handleAddOverlay = () => {
    if (!content.overlay) {
      updateContent('overlay', {
        enabled: true,
        color: 'rgba(0,0,0,0.4)',
        blendMode: 'normal'
      });
    } else {
      updateContent('overlay', {
        ...content.overlay,
        enabled: !content.overlay.enabled
      });
    }
  };

  // Preset options for aspect ratio
  const aspectRatioOptions = [
    { label: 'Original', value: 'auto'},
    { label: 'Square', value: '1/1' },
    { label: 'Wide Screen', value: '16/9'},
    { label: 'Classic', value: '4/3'},
    { label: 'Portrait', value: '3/4'},
    { label: 'Story', value: '9/16'},
    { label: 'Custom', value: 'custom'}
  ];

  // Image effects options
  const effectOptions = [
    { label: 'Original', value: 'none'},
    { label: 'Black & White', value: 'grayscale(100%)' },
    { label: 'Vintage', value: 'sepia(100%)'},
    { label: 'Soft Focus', value: 'blur(3px)'},
    { label: 'Bright', value: 'brightness(120%)'},
    { label: 'High Contrast', value: 'contrast(150%)' },
    { label: 'Warm Tone', value: 'sepia(30%)'},
    { label: 'Cool Tone', value: 'hue-rotate(30deg)' }
  ];

  // Animation options
  const animationOptions = [
    { name: 'None', value: 'none', class: 'transform-none' },
    { name: 'Fade In', value: 'fadeIn', class: 'opacity-0 hover:opacity-100' },
    { name: 'Slide Up', value: 'slideUp', class: 'transform translate-y-2 hover:translate-y-0' },
    { name: 'From Left', value: 'slideInLeft', class: 'transform -translate-x-2 hover:translate-x-0' },
    { name: 'From Right', value: 'slideInRight', class: 'transform translate-x-2 hover:-translate-x-0' },
    { name: 'Zoom In', value: 'zoomIn', class: 'transform scale-95 hover:scale-100' },
    { name: 'Pulse', value: 'pulse', class: 'transform hover:scale-105' }
  ];

  // Size options
  const sizeOptions = [
    { value: 'small', name: 'Small', class: 'max-w-xs' },
    { value: 'medium', name: 'Medium', class: 'max-w-lg' },
    { value: 'large', name: 'Large', class: 'max-w-2xl' },
    { value: 'full', name: 'Full Width', class: 'w-full' }
  ];

  const alignmentOptions = [
    { value: 'left', icon: AlignLeft, name: 'Left' },
    { value: 'center', icon: AlignCenter, name: 'Center' },
    { value: 'right', icon: AlignRight, name: 'Right' }
  ];

  const shadowOptions = [
    { name: 'None', value: '', class: 'shadow-none' },
    { name: 'Subtle', value: 'shadow-sm', class: 'shadow-sm' },
    { name: 'Medium', value: 'shadow', class: 'shadow' },
    { name: 'Strong', value: 'shadow-lg', class: 'shadow-lg' }
  ];

  const borderRadiusOptions = [
    { name: 'Sharp', value: '0', class: 'rounded-none' },
    { name: 'Slight', value: '4px', class: 'rounded-sm' },
    { name: 'Smooth', value: '8px', class: 'rounded' },
    { name: 'Curved', value: '16px', class: 'rounded-lg' },
    { name: 'Circular', value: '9999px', class: 'rounded-full' }
  ];

  return (
    <div className="max-w-6xl mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <Image className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Image Designer</h2>
              <p className="text-indigo-100">Create beautiful images with our easy-to-use visual editor</p>
            </div>
          </div>
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="bg-white/20 hover:bg-white/30 transition-colors px-4 py-2 rounded-lg flex items-center gap-2"
          >
            {showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            {showPreview ? 'Hide' : 'Show'} Preview
          </button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row">
        {/* Form Section */}
        <div className="flex-1 p-6">
          {/* Navigation Tabs */}
          <div className="flex bg-gray-100 rounded-xl p-1 mb-6">
            {[
              { id: 'basic', label: 'Basic Setup', icon: Upload },
              { id: 'layout', label: 'Layout & Size', icon: Settings },
              { id: 'style', label: 'Style & Effects', icon: Palette }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveSection(tab.id)}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
                  activeSection === tab.id
                    ? 'bg-white text-indigo-600 shadow-md'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Basic Setup Tab */}
          {activeSection === 'basic' && (
            <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">
              <div className="space-y-4">
                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
                    <Sparkles className="w-4 h-4 text-indigo-500" />
                    Image Source
                  </label>
                  
                  {content?.imageUrl ? (
                    <div className={`space-y-4 ${isAnimating ? 'animate-pulse' : ''}`}>
                      <div className="p-4 border-2 border-gray-200 rounded-xl bg-gray-50">
                        <p className="text-sm font-medium mb-3 text-gray-700">Current Image:</p>
                        <img 
                          src={content.imageUrl} 
                          alt="Image Preview" 
                          className="max-w-full h-48 object-cover rounded-lg mx-auto shadow-lg"
                        />
                      </div>
                      <button
                        onClick={() => updateContent('imageUrl', '')}
                        className="bg-red-100 text-red-700 px-4 py-2 rounded-lg hover:bg-red-200 transition-colors text-sm font-medium"
                      >
                        Remove Image
                      </button>
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Image className="w-8 h-8 text-gray-400" />
                      </div>
                      <p className="text-lg font-medium text-gray-700 mb-2">Add your image</p>
                      <p className="text-sm text-gray-500 mb-4">Use the upload button or enter a URL below</p>
                    </div>
                  )}

                  <div className="mt-4 space-y-4">
                    <input
                      type="text"
                      className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-0 transition-colors"
                      placeholder="Paste your image URL here..."
                      value={content?.imageUrl || ''}
                      onChange={(e) => updateContent('imageUrl', e.target.value)}
                    />
                    
                    <div className="bg-indigo-50 p-4 rounded-lg">
                      <p className="text-sm font-medium text-indigo-800 mb-2">Upload from device:</p>
                      <ImageUploader onImageUpload={handleImageUpload} pageSlug={pageSlug} />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
                      <Link2 className="w-4 h-4 text-indigo-500" />
                      Link (Optional)
                    </label>
                    <input
                      type="text"
                      className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-0 transition-colors"
                      placeholder="Where should this image link to?"
                      value={content?.linkUrl || ''}
                      onChange={(e) => updateContent('linkUrl', e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
                      <Type className="w-4 h-4 text-indigo-500" />
                      Alt Text (Accessibility)
                    </label>
                    <input
                      type="text"
                      className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-0 transition-colors"
                      placeholder="Describe your image..."
                      value={content?.imageAlt || ''}
                      onChange={(e) => updateContent('imageAlt', e.target.value)}
                    />
                  </div>
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
                    <Type className="w-4 h-4 text-indigo-500" />
                    Caption (Optional)
                  </label>
                  <input
                    type="text"
                    className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-0 transition-colors"
                    placeholder="Add a caption below your image..."
                    value={content?.caption || ''}
                    onChange={(e) => updateContent('caption', e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Layout & Size Tab */}
          {activeSection === 'layout' && (
            <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">
              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-4">
                  <AlignCenter className="w-4 h-4 text-indigo-500" />
                  Image Alignment
                </label>
                <div className="flex bg-gray-100 rounded-xl p-1">
                  {alignmentOptions.map(option => (
                    <button
                      key={option.value}
                      onClick={() => updateContent('alignment', option.value)}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
                        content?.alignment === option.value || (!content?.alignment && option.value === 'center')
                          ? 'bg-white text-indigo-600 shadow-md'
                          : 'text-gray-600 hover:text-gray-800'
                      }`}
                    >
                      <option.icon className="w-4 h-4" />
                      {option.name}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-4">
                  <Settings className="w-4 h-4 text-indigo-500" />
                  Image Size
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {sizeOptions.map(size => (
                    <button
                      key={size.value}
                      onClick={() => updateContent('size', size.value)}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        content?.size === size.value || (!content?.size && size.value === 'medium')
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="text-left">
                        <p className="font-medium text-sm mb-2">{size.name}</p>
                        <div className={`${size.class} `} style={{maxWidth: '100px'}}></div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-4">
                  <Settings className="w-4 h-4 text-indigo-500" />
                  Aspect Ratio
                </label>
                <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                  {aspectRatioOptions.map(option => (
                    <button
                      key={option.value}
                      onClick={() => updateContent('aspectRatio', option.value)}
                      className={`p-3 rounded-xl border-2 transition-all text-center ${
                        content?.aspectRatio === option.value || (!content?.aspectRatio && option.value === 'auto')
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="text-2xl mb-1">{option.icon}</div>
                      <div className="text-xs font-medium">{option.label}</div>
                    </button>
                  ))}
                </div>
                
                {content?.aspectRatio === 'custom' && (
                  <div className="mt-4 flex gap-2 items-center justify-center">
                    <input
                      type="text"
                      className="w-16 p-2 border-2 border-gray-200 rounded text-center focus:border-indigo-500 focus:ring-0"
                      value={content?.aspectRatioWidth || '16'}
                      onChange={(e) => updateContent('aspectRatioWidth', e.target.value)}
                    />
                    <span className="font-bold text-gray-600">:</span>
                    <input
                      type="text"
                      className="w-16 p-2 border-2 border-gray-200 rounded text-center focus:border-indigo-500 focus:ring-0"
                      value={content?.aspectRatioHeight || '9'}
                      onChange={(e) => updateContent('aspectRatioHeight', e.target.value)}
                    />
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-3 block">Custom Width</label>
                  <div className="flex">
                    <input
                      type="text"
                      className="flex-1 p-3 border-2 border-gray-200 rounded-l-xl focus:border-indigo-500 focus:ring-0"
                      value={content?.width || '100%'}
                      onChange={(e) => updateContent('width', e.target.value)}
                      placeholder="100%, 500px, auto"
                    />
                    <select
                      className="border-l-0 border-2 border-gray-200 p-3 rounded-r-xl bg-white focus:border-indigo-500 focus:ring-0"
                      value={content?.width?.includes('%') ? '%' : content?.width?.includes('px') ? 'px' : 'auto'}
                      onChange={(e) => {
                        let value = content?.width || '100';
                        if (value.includes('%') || value.includes('px')) {
                          value = parseInt(value) || 100;
                        }
                        
                        if (e.target.value === 'auto') {
                          updateContent('width', 'auto');
                        } else {
                          updateContent('width', `${value}${e.target.value}`);
                        }
                      }}
                    >
                      <option value="%">%</option>
                      <option value="px">px</option>
                      <option value="auto">auto</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-3 block">Max Height</label>
                  <div className="flex">
                    <input
                      type="text"
                      className="flex-1 p-3 border-2 border-gray-200 rounded-l-xl focus:border-indigo-500 focus:ring-0"
                      value={content?.maxHeight?.replace('px', '') || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '') {
                          updateContent('maxHeight', 'none');
                        } else {
                          updateContent('maxHeight', `${value}px`);
                        }
                      }}
                      placeholder="Leave empty for no limit"
                    />
                    <div className="border-l-0 border-2 border-gray-200 p-3 rounded-r-xl bg-gray-100">
                      px
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Style & Effects Tab */}
          {activeSection === 'style' && (
            <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">
              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-4">
                  <Palette className="w-4 h-4 text-indigo-500" />
                  Visual Effects
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {effectOptions.map(effect => (
                    <button
                      key={effect.value}
                      onClick={() => updateContent('effect', effect.value)}
                      className={`p-3 rounded-xl border-2 transition-all text-center ${
                        content?.effect === effect.value || (!content?.effect && effect.value === 'none')
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="text-2xl mb-1">{effect.icon}</div>
                      <div className="text-xs font-medium">{effect.label}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-4">
                  <Sparkles className="w-4 h-4 text-indigo-500" />
                  Animation
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {animationOptions.map(animation => (
                    <button
                      key={animation.value}
                      onClick={() => updateContent('animation', animation.value)}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        content?.animation === animation.value || (!content?.animation && animation.value === 'none')
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="text-left">
                        <p className="font-medium text-sm">{animation.name}</p>
                        <div className={`${animation.class} bg-gray-200 h-2 rounded mt-2 transition-all duration-300`}></div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-4">
                    <Settings className="w-4 h-4 text-indigo-500" />
                    Border Radius
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {borderRadiusOptions.map(radius => (
                      <button
                        key={radius.value}
                        onClick={() => updateContent('borderRadius', radius.value)}
                        className={`p-3 rounded-xl border-2 transition-all text-center ${
                          content?.borderRadius === radius.value || (!content?.borderRadius && radius.value === '0')
                            ? 'border-indigo-500 bg-indigo-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className={`w-8 h-6 bg-gradient-to-r from-indigo-400 to-purple-400 mx-auto mb-1 ${radius.class}`}></div>
                        <div className="text-xs font-medium">{radius.name}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-4">
                    <Settings className="w-4 h-4 text-indigo-500" />
                    Shadow Effect
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {shadowOptions.map(shadow => (
                      <button
                        key={shadow.value}
                        onClick={() => updateContent('shadow', shadow.value)}
                        className={`p-3 rounded-xl border-2 transition-all text-center ${
                          content?.shadow === shadow.value || (!content?.shadow && shadow.value === '')
                            ? 'border-indigo-500 bg-indigo-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className={`w-8 h-6 bg-gradient-to-r from-indigo-400 to-purple-400 mx-auto mb-1 rounded ${shadow.class}`}></div>
                        <div className="text-xs font-medium">{shadow.name}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <label className="flex items-center space-x-3 mb-4">
                  <input
                    type="checkbox"
                    className="w-5 h-5 text-indigo-600 border-2 border-gray-300 rounded focus:ring-indigo-500"
                    checked={content?.overlay?.enabled || false}
                    onChange={handleAddOverlay}
                  />
                  <span className="text-sm font-semibold text-gray-700">Add Color Overlay</span>
                </label>
                
                {content?.overlay?.enabled && (
                  <div className="space-y-3 pl-8">
                    <div>
                      <label className="block text-sm font-medium mb-1 text-gray-700">Overlay Color</label>
                      <input
                        type="text"
                        className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-0 transition-colors"
                        value={content?.overlay?.color || 'rgba(0,0,0,0.4)'}
                        onChange={(e) => updateContent('overlay', {
                          ...content.overlay,
                          color: e.target.value
                        })}
                        placeholder="rgba(0,0,0,0.4)"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-1 text-gray-700">Blend Mode</label>
                      <select
                        className="w-full p-3 border-2 border-gray-200 rounded-xl bg-white focus:border-indigo-500 focus:ring-0 transition-colors"
                        value={content?.overlay?.blendMode || 'normal'}
                        onChange={(e) => updateContent('overlay', {
                          ...content.overlay,
                          blendMode: e.target.value
                        })}
                      >
                        <option value="normal">Normal</option>
                        <option value="multiply">Multiply</option>
                        <option value="screen">Screen</option>
                        <option value="overlay">Overlay</option>
                        <option value="darken">Darken</option>
                        <option value="lighten">Lighten</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Preview Section */}
        {showPreview && (
          <div className="lg:w-96 bg-gray-50 p-6 border-l">
            <div className="sticky top-6">
              <h3 className="font-bold text-lg mb-4 text-gray-800">Live Preview</h3>
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className={`p-8 text-${content?.alignment || 'center'} bg-gradient-to-r from-gray-100 to-gray-200`}>
                  {content?.imageUrl ? (
                    <div className="space-y-4">
                      <div
                        className={`relative inline-block transition-all duration-300 ${
                          content?.size === 'small' ? 'max-w-xs' : 
                          content?.size === 'large' ? 'max-w-2xl' : 
                          content?.size === 'full' ? 'w-full' : 'max-w-lg'
                        }`}
                        style={{
                          width: content?.width || '100%',
                          maxHeight: content?.maxHeight || 'none',
                          aspectRatio: content?.aspectRatio && content.aspectRatio !== 'auto' ? 
                            (content.aspectRatio === 'custom' ? 
                              `${content.aspectRatioWidth || 16}/${content.aspectRatioHeight || 9}` : 
                              content.aspectRatio) : 'auto',
                          boxShadow: content?.shadow === 'shadow-sm' ? '0 1px 2px 0 rgba(0, 0, 0, 0.05)' : 
                                    content?.shadow === 'shadow' ? '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)' :
                                    content?.shadow === 'shadow-lg' ? '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)' : 'none',
                          borderRadius: content?.borderRadius || '0',
                          overflow: 'hidden'
                        }}
                      >
                        <img 
                          src={content.imageUrl}
                          alt={content.imageAlt || 'Image Preview'}
                          className={`w-full h-full object-cover transition-all duration-300 ${content?.effect || ''} ${content?.animation ? `animate-${content.animation}` : ''}`}
                        />
                        {content?.overlay?.enabled && (
                          <div
                            className="absolute inset-0"
                            style={{
                              backgroundColor: content.overlay.color || 'rgba(0,0,0,0.4)',
                              mixBlendMode: content.overlay.blendMode || 'normal'
                            }}
                          ></div>
                        )}
                      </div>
                      {content?.caption && (
                        <p className="mt-4 text-sm text-gray-600 text-center">{content.caption}</p>
                      )}
                    </div>
                  ) : (
                    <div className="text-center text-gray-400">
                      <p className="text-lg font-medium mb-2">No image selected</p>
                      <p className="text-sm">Upload an image to see the preview here</p>
                    </div>
                  )}
                </div>
                <div className="p-4 bg-gray-50 border-t">
                  <p className="text-xs text-gray-500">Preview may not reflect all settings exactly</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
export default ImageForm;













// // src/components/admin/page-management/section-forms/ImageForm.js
// import React, { useState, useEffect } from 'react';
// import ImageUploader from '../ImageUploader';

// const ImageForm = ({ content, updateContent, pageSlug }) => {
//   const [previewMode, setPreviewMode] = useState(false);
//   const [showAdvanced, setShowAdvanced] = useState(false);
  
//   // Effects
//   const handleImageUpload = (imagePath) => {
//     console.log('ImageForm handling image upload with path:', imagePath);
//     updateContent('imageUrl', imagePath);
//   };

//   // Generate image preview URL
//   const imagePreviewUrl = content?.imageUrl || '';
  
//   // Handle image overlay
//   const handleAddOverlay = () => {
//     if (!content.overlay) {
//       updateContent('overlay', {
//         enabled: true,
//         color: 'rgba(0,0,0,0.4)',
//         blendMode: 'normal'
//       });
//     } else {
//       updateContent('overlay', {
//         ...content.overlay,
//         enabled: !content.overlay.enabled
//       });
//     }
//   };
  
//   // Preset options for aspect ratio
//   const aspectRatioOptions = [
//     { label: 'Original', value: 'auto' },
//     { label: 'Square (1:1)', value: '1/1' },
//     { label: 'Landscape (16:9)', value: '16/9' },
//     { label: 'Landscape (4:3)', value: '4/3' },
//     { label: 'Portrait (3:4)', value: '3/4' },
//     { label: 'Portrait (9:16)', value: '9/16' },
//     { label: 'Custom', value: 'custom' }
//   ];
  
//   // Image effects options
//   const effectOptions = [
//     { label: 'None', value: 'none' },
//     { label: 'Grayscale', value: 'grayscale(100%)' },
//     { label: 'Sepia', value: 'sepia(100%)' },
//     { label: 'Blur', value: 'blur(3px)' },
//     { label: 'Brighten', value: 'brightness(120%)' },
//     { label: 'Contrast', value: 'contrast(150%)' },
//     { label: 'Warm', value: 'sepia(30%)' },
//     { label: 'Cool', value: 'hue-rotate(30deg)' }
//   ];
  
//   // Animation options
//   const animationOptions = [
//     { label: 'None', value: 'none' },
//     { label: 'Fade In', value: 'fadeIn' },
//     { label: 'Slide Up', value: 'slideUp' },
//     { label: 'Slide In From Left', value: 'slideInLeft' },
//     { label: 'Slide In From Right', value: 'slideInRight' },
//     { label: 'Zoom In', value: 'zoomIn' },
//     { label: 'Pulse', value: 'pulse' }
//   ];
  
//   // Border style options
//   const borderStyleOptions = [
//     { label: 'None', value: 'none' },
//     { label: 'Solid', value: 'solid' },
//     { label: 'Dashed', value: 'dashed' },
//     { label: 'Dotted', value: 'dotted' },
//     { label: 'Double', value: 'double' }
//   ];

//   // Interactive border radius slider
//   const BorderRadiusSelector = () => {
//     const currentRadius = content.borderRadius || '0';
    
//     const presetValues = [
//       { label: 'None', value: '0' },
//       { label: 'Slight', value: '4px' },
//       { label: 'Medium', value: '8px' },
//       { label: 'Large', value: '16px' },
//       { label: 'Round', value: '9999px' }
//     ];
    
//     return (
//       <div className="mt-2">
//         <div className="flex flex-wrap gap-2">
//           {presetValues.map(preset => (
//             <button
//               key={preset.value}
//               type="button"
//               className={`px-3 py-1 text-xs border rounded hover:bg-gray-100 
//               ${currentRadius === preset.value ? 'bg-blue-100 border-blue-400' : 'border-gray-300'}`}
//               onClick={() => updateContent('borderRadius', preset.value)}
//             >
//               {preset.label}
//             </button>
//           ))}
//         </div>
//       </div>
//     );
//   };
  
//   return (
//     <div>
//       <h3 className="font-bold text-lg mb-4">Image Section</h3>
      
//       {/* Image Source Input */}
//       <div className="mb-6 bg-white p-4 border rounded-md shadow-sm">
//         <h4 className="font-medium text-gray-700 mb-3">Image Source</h4>
        
//         <div className="mb-4 flex gap-1">
//           <div className="flex-1">
//             <label className="block text-sm font-medium mb-1 text-gray-700">Image URL</label>
//             <input
//               type="text"
//               className="w-full p-2 border rounded bg-white"
//               value={content?.imageUrl || ''}
//               onChange={(e) => updateContent('imageUrl', e.target.value)}
//               placeholder="Enter image URL or upload below"
//             />
//           </div>
          
//           <div className="ml-1">
//             <label className="block text-sm font-medium mb-1 text-gray-700">Link (Optional)</label>
//             <input
//               type="text"
//               className="w-full p-2 border rounded bg-white"
//               value={content?.linkUrl || ''}
//               onChange={(e) => updateContent('linkUrl', e.target.value)}
//               placeholder="URL when image is clicked"
//             />
//           </div>
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1 text-gray-700">Upload Image</label>
//           <ImageUploader 
//             onImageUpload={handleImageUpload} 
//             pageSlug={pageSlug}
//           />
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1 text-gray-700">Alt Text</label>
//           <input
//             type="text"
//             className="w-full p-2 border rounded bg-white"
//             value={content?.imageAlt || ''}
//             onChange={(e) => updateContent('imageAlt', e.target.value)}
//             placeholder="Describe the image for accessibility"
//           />
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1 text-gray-700">Caption (Optional)</label>
//           <input
//             type="text"
//             className="w-full p-2 border rounded bg-white"
//             value={content?.caption || ''}
//             onChange={(e) => updateContent('caption', e.target.value)}
//             placeholder="Caption displayed below the image"
//           />
//         </div>
//       </div>
      
//       {/* Layout & Sizing */}
//       <div className="mb-6 bg-white p-4 border rounded-md shadow-sm">
//         <h4 className="font-medium text-gray-700 mb-3">Layout & Sizing</h4>
        
//         <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//           <div>
//             <label className="block text-sm font-medium mb-1 text-gray-700">Alignment</label>
//             <div className="flex gap-2">
//               <button
//                 type="button"
//                 className={`flex-1 p-2 border rounded ${content?.alignment === 'left' ? 'bg-blue-100 border-blue-400' : 'hover:bg-gray-50'}`}
//                 onClick={() => updateContent('alignment', 'left')}
//               >
//                 Left
//               </button>
//               <button
//                 type="button"
//                 className={`flex-1 p-2 border rounded ${content?.alignment === 'center' || !content?.alignment ? 'bg-blue-100 border-blue-400' : 'hover:bg-gray-50'}`}
//                 onClick={() => updateContent('alignment', 'center')}
//               >
//                 Center
//               </button>
//               <button
//                 type="button"
//                 className={`flex-1 p-2 border rounded ${content?.alignment === 'right' ? 'bg-blue-100 border-blue-400' : 'hover:bg-gray-50'}`}
//                 onClick={() => updateContent('alignment', 'right')}
//               >
//                 Right
//               </button>
//             </div>
//           </div>
          
//           <div>
//             <label className="block text-sm font-medium mb-1 text-gray-700">Size</label>
//             <div className="flex gap-2">
//               <button
//                 type="button"
//                 className={`flex-1 p-2 border rounded ${content?.size === 'small' ? 'bg-blue-100 border-blue-400' : 'hover:bg-gray-50'}`}
//                 onClick={() => updateContent('size', 'small')}
//               >
//                 Small
//               </button>
//               <button
//                 type="button"
//                 className={`flex-1 p-2 border rounded ${content?.size === 'medium' || !content?.size ? 'bg-blue-100 border-blue-400' : 'hover:bg-gray-50'}`}
//                 onClick={() => updateContent('size', 'medium')}
//               >
//                 Medium
//               </button>
//               <button
//                 type="button"
//                 className={`flex-1 p-2 border rounded ${content?.size === 'large' ? 'bg-blue-100 border-blue-400' : 'hover:bg-gray-50'}`}
//                 onClick={() => updateContent('size', 'large')}
//               >
//                 Large
//               </button>
//               <button
//                 type="button"
//                 className={`flex-1 p-2 border rounded ${content?.size === 'full' ? 'bg-blue-100 border-blue-400' : 'hover:bg-gray-50'}`}
//                 onClick={() => updateContent('size', 'full')}
//               >
//                 Full
//               </button>
//             </div>
//           </div>
//         </div>
        
//         <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
//           <div>
//             <label className="block text-sm font-medium mb-1 text-gray-700">Width</label>
//             <div className="flex">
//               <input
//                 type="text"
//                 className="w-full p-2 border rounded-l bg-white"
//                 value={content?.width || '100%'}
//                 onChange={(e) => updateContent('width', e.target.value)}
//                 placeholder="100%, 500px, auto"
//               />
//               <select
//                 className="border-l-0 border p-2 rounded-r bg-white"
//                 value={content?.width?.includes('%') ? '%' : content?.width?.includes('px') ? 'px' : 'auto'}
//                 onChange={(e) => {
//                   let value = content?.width || '100';
//                   if (value.includes('%') || value.includes('px')) {
//                     value = parseInt(value) || 100;
//                   }
                  
//                   if (e.target.value === 'auto') {
//                     updateContent('width', 'auto');
//                   } else {
//                     updateContent('width', `${value}${e.target.value}`);
//                   }
//                 }}
//               >
//                 <option value="%">%</option>
//                 <option value="px">px</option>
//                 <option value="auto">auto</option>
//               </select>
//             </div>
//           </div>
          
//           <div>
//             <label className="block text-sm font-medium mb-1 text-gray-700">Max Height</label>
//             <div className="flex">
//               <input
//                 type="text"
//                 className="w-full p-2 border rounded-l bg-white"
//                 value={content?.maxHeight?.replace('px', '') || ''}
//                 onChange={(e) => {
//                   const value = e.target.value;
//                   if (value === '') {
//                     updateContent('maxHeight', 'none');
//                   } else {
//                     updateContent('maxHeight', `${value}px`);
//                   }
//                 }}
//                 placeholder="Leave empty for no limit"
//               />
//               <div className="border-l-0 border p-2 rounded-r bg-gray-100">
//                 px
//               </div>
//             </div>
//           </div>
//         </div>
        
//         <div className="mt-4">
//           <label className="block text-sm font-medium mb-1 text-gray-700">Aspect Ratio</label>
//           <select
//             className="w-full p-2 border rounded bg-white"
//             value={content?.aspectRatio || 'auto'}
//             onChange={(e) => updateContent('aspectRatio', e.target.value)}
//           >
//             {aspectRatioOptions.map(option => (
//               <option key={option.value} value={option.value}>
//                 {option.label}
//               </option>
//             ))}
//           </select>
          
//           {content?.aspectRatio === 'custom' && (
//             <div className="mt-2 flex gap-2 items-center">
//               <input
//                 type="text"
//                 className="w-20 p-2 border rounded bg-white"
//                 value={content?.aspectRatioWidth || '16'}
//                 onChange={(e) => updateContent('aspectRatioWidth', e.target.value)}
//               />
//               <span>:</span>
//               <input
//                 type="text"
//                 className="w-20 p-2 border rounded bg-white"
//                 value={content?.aspectRatioHeight || '9'}
//                 onChange={(e) => updateContent('aspectRatioHeight', e.target.value)}
//               />
//             </div>
//           )}
//         </div>
        
//         <div className="mt-4">
//           <label className="block text-sm font-medium mb-1 text-gray-700">Object Fit</label>
//           <div className="flex gap-2">
//             <button
//               type="button"
//               className={`flex-1 p-2 border rounded ${content?.objectFit === 'cover' || !content?.objectFit ? 'bg-blue-100 border-blue-400' : 'hover:bg-gray-50'}`}
//               onClick={() => updateContent('objectFit', 'cover')}
//             >
//               Cover
//             </button>
//             <button
//               type="button"
//               className={`flex-1 p-2 border rounded ${content?.objectFit === 'contain' ? 'bg-blue-100 border-blue-400' : 'hover:bg-gray-50'}`}
//               onClick={() => updateContent('objectFit', 'contain')}
//             >
//               Contain
//             </button>
//             <button
//               type="button"
//               className={`flex-1 p-2 border rounded ${content?.objectFit === 'fill' ? 'bg-blue-100 border-blue-400' : 'hover:bg-gray-50'}`}
//               onClick={() => updateContent('objectFit', 'fill')}
//             >
//               Fill
//             </button>
//           </div>
//         </div>
//       </div>
      
//       {/* Styling */}
//       <div className="mb-6 bg-white p-4 border rounded-md shadow-sm">
//         <div className="flex justify-between items-center">
//           <h4 className="font-medium text-gray-700">Styling & Effects</h4>
//           <button
//             type="button"
//             className="text-sm text-blue-600 hover:text-blue-800"
//             onClick={() => setShowAdvanced(!showAdvanced)}
//           >
//             {showAdvanced ? 'Hide Advanced' : 'Show Advanced'}
//           </button>
//         </div>
        
//         <div className="mt-3">
//           <label className="block text-sm font-medium mb-1 text-gray-700">Border Radius</label>
//           <BorderRadiusSelector />
//         </div>
        
//         <div className="mt-4">
//           <label className="block text-sm font-medium mb-1 text-gray-700">Shadow</label>
//           <div className="flex gap-2">
//             <button
//               type="button"
//               className={`flex-1 p-2 border rounded ${!content?.shadow ? 'bg-blue-100 border-blue-400' : 'hover:bg-gray-50'}`}
//               onClick={() => updateContent('shadow', '')}
//             >
//               None
//             </button>
//             <button
//               type="button"
//               className={`flex-1 p-2 border rounded ${content?.shadow === 'shadow-sm' ? 'bg-blue-100 border-blue-400' : 'hover:bg-gray-50'}`}
//               onClick={() => updateContent('shadow', 'shadow-sm')}
//             >
//               Small
//             </button>
//             <button
//               type="button"
//               className={`flex-1 p-2 border rounded ${content?.shadow === 'shadow' ? 'bg-blue-100 border-blue-400' : 'hover:bg-gray-50'}`}
//               onClick={() => updateContent('shadow', 'shadow')}
//             >
//               Medium
//             </button>
//             <button
//               type="button"
//               className={`flex-1 p-2 border rounded ${content?.shadow === 'shadow-lg' ? 'bg-blue-100 border-blue-400' : 'hover:bg-gray-50'}`}
//               onClick={() => updateContent('shadow', 'shadow-lg')}
//             >
//               Large
//             </button>
//           </div>
//         </div>
        
//         {showAdvanced && (
//           <>
//             <div className="mt-4">
//               <label className="block text-sm font-medium mb-1 text-gray-700">Border</label>
//               <div className="grid grid-cols-2 gap-2">
//                 <div>
//                   <select
//                     className="w-full p-2 border rounded bg-white"
//                     value={content?.borderStyle || 'none'}
//                     onChange={(e) => updateContent('borderStyle', e.target.value)}
//                   >
//                     {borderStyleOptions.map(option => (
//                       <option key={option.value} value={option.value}>
//                         {option.label}
//                       </option>
//                     ))}
//                   </select>
//                 </div>
//                 <div>
//                   <input
//                     type="text"
//                     className="w-full p-2 border rounded bg-white"
//                     value={content?.borderWidth || '1px'}
//                     onChange={(e) => updateContent('borderWidth', e.target.value)}
//                     placeholder="1px"
//                     disabled={content?.borderStyle === 'none'}
//                   />
//                 </div>
//               </div>
              
//               {content?.borderStyle !== 'none' && (
//                 <div className="mt-2">
//                   <input
//                     type="text"
//                     className="w-full p-2 border rounded bg-white"
//                     value={content?.borderColor || '#000000'}
//                     onChange={(e) => updateContent('borderColor', e.target.value)}
//                     placeholder="Border color (hex or name)"
//                   />
//                 </div>
//               )}
//             </div>
            
//             <div className="mt-4">
//               <label className="flex items-center mb-2">
//                 <input
//                   type="checkbox"
//                   className="mr-2"
//                   checked={content?.overlay?.enabled || false}
//                   onChange={handleAddOverlay}
//                 />
//                 <span className="text-sm font-medium text-gray-700">Add Overlay</span>
//               </label>
              
//               {content?.overlay?.enabled && (
//                 <>
//                   <input
//                     type="text"
//                     className="w-full p-2 border rounded bg-white mb-2"
//                     value={content?.overlay?.color || 'rgba(0,0,0,0.4)'}
//                     onChange={(e) => updateContent('overlay', {
//                       ...content.overlay,
//                       color: e.target.value
//                     })}
//                     placeholder="rgba(0,0,0,0.4)"
//                   />
                  
//                   <select
//                     className="w-full p-2 border rounded bg-white"
//                     value={content?.overlay?.blendMode || 'normal'}
//                     onChange={(e) => updateContent('overlay', {
//                       ...content.overlay,
//                       blendMode: e.target.value
//                     })}
//                   >
//                     <option value="normal">Normal</option>
//                     <option value="multiply">Multiply</option>
//                     <option value="screen">Screen</option>
//                     <option value="overlay">Overlay</option>
//                     <option value="darken">Darken</option>
//                     <option value="lighten">Lighten</option>
//                     <option value="color-dodge">Color Dodge</option>
//                     <option value="color-burn">Color Burn</option>
//                     <option value="hard-light">Hard Light</option>
//                     <option value="soft-light">Soft Light</option>
//                     <option value="difference">Difference</option>
//                     <option value="exclusion">Exclusion</option>
//                   </select>
//                 </>
//               )}
//             </div>
            
//             <div className="mt-4">
//               <label className="block text-sm font-medium mb-1 text-gray-700">Visual Effects</label>
//               <select
//                 className="w-full p-2 border rounded bg-white"
//                 value={content?.effect || 'none'}
//                 onChange={(e) => updateContent('effect', e.target.value)}
//               >
//                 {effectOptions.map(option => (
//                   <option key={option.value} value={option.value}>
//                     {option.label}
//                   </option>
//                 ))}
//               </select>
//             </div>
            
//             <div className="mt-4">
//               <label className="block text-sm font-medium mb-1 text-gray-700">Animation</label>
//               <select
//                 className="w-full p-2 border rounded bg-white"
//                 value={content?.animation || 'none'}
//                 onChange={(e) => updateContent('animation', e.target.value)}
//               >
//                 {animationOptions.map(option => (
//                   <option key={option.value} value={option.value}>
//                     {option.label}
//                   </option>
//                 ))}
//               </select>
//             </div>
//           </>
//         )}
//       </div>
      
//       {/* Image Preview */}
//       {content?.imageUrl && (
//         <div className="mt-6 border p-4 rounded-md">
//           <div className="flex justify-between items-center mb-4">
//             <h4 className="font-medium text-gray-700">Preview</h4>
//             <button
//               type="button"
//               className="text-sm text-blue-600 hover:text-blue-800"
//               onClick={() => setPreviewMode(!previewMode)}
//             >
//               {previewMode ? 'Compact View' : 'Expanded View'}
//             </button>
//           </div>
          
//           <div 
//             className={`relative overflow-hidden bg-gray-100 p-2 ${previewMode ? 'h-[400px]' : 'h-60'}`}
//             style={{
//               textAlign: content?.alignment || 'center',
//               padding: '16px'
//             }}
//           >
//             <div
//               className={`relative inline-block ${content?.size === 'small' ? 'max-w-xs' : content?.size === 'large' ? 'max-w-2xl' : content?.size === 'full' ? 'w-full' : 'max-w-lg'}`}
//               style={{
//                 width: content?.width || '100%',
//                 maxHeight: content?.maxHeight || 'none',
//                 aspectRatio: content?.aspectRatio && content.aspectRatio !== 'auto' ? content.aspectRatio.replace('/', '/') : 'auto',
//                 boxShadow: content?.shadow === 'shadow-sm' ? '0 1px 2px 0 rgba(0, 0, 0, 0.05)' : 
//                           content?.shadow === 'shadow' ? '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)' :
//                           content?.shadow === 'shadow-lg' ? '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)' : 'none',
//                 borderRadius: content?.borderRadius || '0',
//                 overflow: 'hidden',
//                 border: content?.borderStyle && content.borderStyle !== 'none' ? 
//                         `${content.borderWidth || '1px'} ${content.borderStyle} ${content.borderColor || '#000'}` : 'none'
//               }}
//             >
//               <img 
//                 src={imagePreviewUrl}
//                 alt={content?.imageAlt || 'Preview'} 
//                 className="w-full h-full"
//                 style={{
//                   objectFit: content?.objectFit || 'cover',
//                   filter: content?.effect !== 'none' ? content.effect : 'none',
//                   animation: content?.animation !== 'none' && content.animation ? `${content.animation} 1s` : 'none',
//                 }}
//               />
              
//               {content?.overlay?.enabled && (
//                 <div 
//                   className="absolute inset-0"
//                   style={{
//                     backgroundColor: content.overlay.color || 'rgba(0,0,0,0.4)',
//                     mixBlendMode: content.overlay.blendMode || 'normal'
//                   }}
//                 ></div>
//               )}
//             </div>
            
//             {content?.caption && (
//               <div className="mt-2 text-center text-gray-600 text-sm">
//                 {content.caption}
//               </div>
//             )}
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default ImageForm;
